import styled from 'styled-components';
import { Divider } from '../Divider';
import { Box, LinearProgress } from '@mui/material';
import COLORS from '../../colors';
import { SummaryContainer } from '../containers';
import {
  DURATION_SECONDS,
  RIGHT_SECTION_LABEL,
  TWO_DECIMAL_PLACES
} from '../../constants';
import mapBurstToNumber from '../BudgetSplitSection/mapBurstToNumber';
import { RootState } from '../../redux/store';
import { useAppSelector } from '../../redux/hooks';

interface BudgetSummaryProps {
  label: RIGHT_SECTION_LABEL;
  usedValue: number;
  totalValue: number;
  durations: Record<DURATION_SECONDS, boolean>;
}

interface PercentageUsedProps {
  isOverBudgeted?: boolean;
}

const Budget = styled.div`
  position: relative;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_200};
`;

const DurationCost = styled.div`
  position: relative;
  line-height: 24px;
`;

const BudgetAmountContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
`;

const Percentage = styled.div`
  position: relative;
  line-height: 16px;
  font-weight: 500;
`;

const PercentagePill = styled.div<PercentageUsedProps>`
  border-radius: 4px;
  background-color: ${(props) =>
    props.isOverBudgeted ? COLORS.ERROR : COLORS.SECONDARY_CONTAINER};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 8px;
  font-size: 14px;
  color: ${(props) =>
    props.isOverBudgeted ? COLORS.BLACK : COLORS.NAV_ACTIVE};
  font-family: 'Helvetica Neue';
`;

const Header = styled.div`
  align-self: stretch;
  height: 24px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

const DurationContent = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

const Group = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 4px;
`;

const BreakdownContainer = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
`;

const Frame = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;

const RemainingBudgetParent = styled.div<PercentageUsedProps>`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  color: ${(props) =>
    props.isOverBudgeted ? COLORS.ERROR : COLORS.PRIMARY_500};
`;

const Breakdown = styled.div`
  align-self: stretch;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 12px;
  z-index: 2;
`;

const BudgetParent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  gap: 16px;
`;

const BudgetSummaryContainer = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;

const StyledLinearProgress = styled(LinearProgress)<PercentageUsedProps>`
  &.MuiLinearProgress-root {
    background-color: ${COLORS.PRIMARY_CONTAINER};
  }

  .MuiLinearProgress-bar {
    background-color: ${(props) =>
      props.isOverBudgeted ? COLORS.ERROR : COLORS.PRIMARY_500};
  }
`;

const BudgetSummary = ({
  label,
  usedValue,
  totalValue,
  durations
}: BudgetSummaryProps) => {
  const { burst } = useAppSelector((state: RootState) => state);

  const calculatePercentageUsed = (): number => {
    const used = usedValue / totalValue;

    if (isFinite(used)) {
      return used * 100;
    }

    return 0;
  };

  const calculateProgress = () => {
    const progress = calculatePercentageUsed();
    if (progress > 100) {
      return 100;
    }

    return progress;
  };

  const displayBurstTotal = (duration: DURATION_SECONDS): number => {
    if (label === RIGHT_SECTION_LABEL.BUDGET) {
      return burst[duration].calculatedTotalBudget;
    }

    if (label === RIGHT_SECTION_LABEL.TARPS) {
      return burst[duration].calculatedTotalTarps;
    }

    if (label === RIGHT_SECTION_LABEL.IMPACT) {
      return burst[duration].calculatedTotalImpact;
    }

    return -1;
  };

  const mapRowName = (): string => {
    switch (label) {
      case RIGHT_SECTION_LABEL.BUDGET:
        return 'Duration Cost';
      case RIGHT_SECTION_LABEL.TARPS:
        return 'TARPs';
      case RIGHT_SECTION_LABEL.IMPACT:
        return "000's";
      default:
        return '';
    }
  };

  const format = (amount: number): string => {
    const formattedResult = amount
      .toFixed(TWO_DECIMAL_PLACES)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (label === RIGHT_SECTION_LABEL.BUDGET) {
      return '$' + formattedResult;
    }

    return formattedResult;
  };

  return (
    <SummaryContainer title={label}>
      <BudgetSummaryContainer>
        <BudgetParent>
          <Header>
            <BudgetAmountContainer>
              <Budget>{format(usedValue)}</Budget>
              <DurationCost>/</DurationCost>
              <DurationCost>{format(totalValue)}</DurationCost>
            </BudgetAmountContainer>
            <PercentagePill isOverBudgeted={calculatePercentageUsed() > 100}>
              <Percentage>
                {Math.round(calculatePercentageUsed()).toFixed(
                  TWO_DECIMAL_PLACES
                )}
                %
              </Percentage>
            </PercentagePill>
          </Header>
          <Box sx={{ width: '100%' }}>
            <StyledLinearProgress
              variant="determinate"
              isOverBudgeted={calculatePercentageUsed() > 100}
              value={calculateProgress()}
            />
          </Box>
          <Breakdown>
            <BreakdownContainer>
              {Object.entries(durations).map(([duration, isBurstSelected]) => {
                const durationSeconds = duration as DURATION_SECONDS;
                const burstNumber = mapBurstToNumber(durationSeconds);
                return (
                  isBurstSelected && (
                    <DurationContent>
                      <DurationCost>
                        {burstNumber.toString()}" {mapRowName()}
                      </DurationCost>
                      <Group>
                        <Budget>
                          {format(displayBurstTotal(durationSeconds))}
                        </Budget>
                      </Group>
                    </DurationContent>
                  )
                );
              })}
            </BreakdownContainer>
            <Divider />
            <BreakdownContainer>
              <DurationContent>
                <DurationCost>Total Used</DurationCost>
                <Group>
                  <Budget>{format(usedValue)}</Budget>
                </Group>
              </DurationContent>
              <DurationContent>
                <DurationCost>Target {label}</DurationCost>
                <DurationCost>{format(totalValue)}</DurationCost>
              </DurationContent>
            </BreakdownContainer>
            <Divider />
            {totalValue !== 0 && (
              <RemainingBudgetParent
                isOverBudgeted={calculatePercentageUsed() > 100}
              >
                <DurationCost>Remaining {label}</DurationCost>
                <Frame>
                  <DurationCost>{format(totalValue - usedValue)}</DurationCost>
                </Frame>
              </RemainingBudgetParent>
            )}
          </Breakdown>
        </BudgetParent>
      </BudgetSummaryContainer>
    </SummaryContainer>
  );
};

export default BudgetSummary;
